import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';
import axios from 'axios';

import MembersGql from '@/extensions/restrictedArea/store/graphql/queries/Members.gql';
import MemberGql from '@/extensions/restrictedArea/store/graphql/queries/Member.gql';
import MembersPaginatedGql from '@/extensions/restrictedArea/store/graphql/queries/MembersPaginated.gql';
import PreviewSampleMembersGql from '@/extensions/restrictedArea/store/graphql/queries/PreviewSampleMembers.gql';
import ValidateMemberEmailGql from '@/extensions/restrictedArea/store/graphql/queries/ValidateMemberEmail.gql';

import CreateMemberGql from '@/extensions/restrictedArea/store/graphql/mutations/CreateMember.gql';
import UpdateMemberGql from '@/extensions/restrictedArea/store/graphql/mutations/UpdateMember.gql';
import DeleteMemberGql from '@/extensions/restrictedArea/store/graphql/mutations/DeleteMember.gql';
import ValidateMemberEmailDeliverabilityGql from '@/extensions/newsletter/store/graphql/mutations/ValidateMemberEmailDeliverability.gql';
import InitializeMemberImportGql from '@/extensions/restrictedArea/store/graphql/mutations/InitializeMemberImport.gql';
import ProcessMemberPaginationBulkActionGql from '@/extensions/restrictedArea/store/graphql/mutations/ProcessMemberPaginationBulkAction.gql';
import ValidateMemberImportCsvGql from '@/extensions/restrictedArea/store/graphql/mutations/ValidateMemberImportCsv.gql';
import MemberApiTokensGql from '@/extensions/restrictedArea/store/graphql/queries/MemberApiTokens.gql';
import CreateMemberApiTokenGql from '@/extensions/restrictedArea/store/graphql/mutations/CreateMemberApiToken.gql';
import DeleteMemberApiTokenGql from '@/extensions/restrictedArea/store/graphql/mutations/DeleteMemberApiToken.gql';

import { ensureFreshToken } from '@/helpers';

const MEMBER_AUTH_ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_MEMBER_AUTH_ROUTE}`;

export default {
    // Sample-record search for the document-template preview drawer, scoped to a
    // single CMS (filters passed as a variable — see PreviewSampleMembers.gql).
    fetchPreviewSamples: ({ cmsId = null, search = null } = {}) =>
        new Promise((resolve, reject) => {
            const filters = [];
            if (cmsId) filters.push({ key: 'cms', match: cmsId });
            if (search) filters.push({ key: 'name', like: `%${search}%` });

            apolloClient
                .query({ query: PreviewSampleMembersGql, fetchPolicy: 'network-only', variables: { filters } })
                .then(({ data }) => resolve(_.get(data, 'EX_RSTRCT_MembersPaginated.items', [])))
                .catch(err => reject(err));
        }),
    autocomplete: (cmsID, query = null) =>
        new Promise((resolve, reject) => {
            let variables = { cms: parseInt(cmsID) };
            if (query) variables.query = query;

            apolloClient
                .query({
                    query: MembersGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_Members))
                .catch(err => reject(err));
        }),
    validateEmail: (email, cmsID = null, excludedMemberID = null) =>
        new Promise((resolve, reject) => {
            let variables = { email };
            if (cmsID) variables.cms = parseInt(cmsID);
            if (excludedMemberID) variables.excludedMemberId = parseInt(excludedMemberID);

            apolloClient
                .query({
                    query: ValidateMemberEmailGql,
                    variables
                    // fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_ValidateMemberEmail))
                .catch(err => reject(err));
        }),
    // fetchList: (cmsID = null) =>
    //     new Promise((resolve, reject) => {
    //         let variables = {};
    //         if (cmsID) variables.cms = cmsID;
    //
    //         apolloClient
    //             .query({
    //                 query: MembersGql,
    //                 variables
    //             })
    //             .then(({ data }) => resolve(data.EX_RSTRCT_Members))
    //             .catch(err => reject(err));
    //     })
    fetchMembersPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: MembersPaginatedGql,
                    variables: {
                        scope: _.get(data, 'scope', 'ex'),
                        type: _.get(data, 'type', 'rstrct_member'),
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'id'),
                        direction: _.get(data, 'variables.direction', 'asc'),
                        link: _.get(data, 'link.route', '/member/'),
                        filters,
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                    }
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_MembersPaginated))
                .catch(err => reject(err));
        }),
    processMemberPaginationBulkAction: (action, data, options) =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            const variables = {
                action,
                filters,
                selection: {
                    type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                    identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                },
                order: _.get(data, 'variables.order', 'id'),
                direction: _.get(data, 'variables.direction', 'asc'),
            };

            if (options && options.fields) variables.fields = options.fields;

            apolloClient
                .mutate({
                    mutation: ProcessMemberPaginationBulkActionGql,
                    variables,
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_ProcessMemberPaginationBulkAction))
                .catch(err => reject(err));
        }),
    fetchMember: variables => {
        if (variables.id) variables.id = parseInt(variables.id);
        if (variables.customer) variables.customer = parseInt(variables.customer);
        if (variables.cms) variables.cms = parseInt(variables.cms);

        return new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: MemberGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_Member))
                .catch(err => reject(err));
        });
    },
    loginAs(id) {
        return ensureFreshToken().then((token) => {
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            return axios.post(`${MEMBER_AUTH_ENDPOINT}login-as`, { id }, config);
        });
    },
    generateImpersonationToken(id) {
        return ensureFreshToken().then((token) => {
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            return axios.post(`${MEMBER_AUTH_ENDPOINT}generate-impersonation-token/${id}`, null, config);
        });
    },
    create: member => {
        if (member === undefined) throw new Error('required argument is missing');

        member = _.removeDeepByKey(member, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateMemberGql,
                    variables: { member }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_RSTRCT_CreateMember);
                })
                .catch(err => reject(err));
        });
    },
    update: member => {
        if (member === undefined) throw new Error('required argument is missing');

        member = _.removeDeepByKey(member, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateMemberGql,
                    variables: { member }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_RSTRCT_UpdateMember);
                })
                .catch(err => reject(err));
        });
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteMemberGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_RSTRCT_DeleteMember);
                })
                .catch(err => reject(err));
        });
    },
    validateEmailDeliverability: (email, cmsId) => {
        if (email === undefined) throw new Error('email is required');
        if (cmsId === undefined) throw new Error('cmsId is required');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ValidateMemberEmailDeliverabilityGql,
                    variables: {
                        email,
                        cms_id: parseInt(cmsId),
                        force: true
                    }
                })
                .then(({ data }) => {
                    resolve(data.EX_NWSLTR_ValidateMemberEmail);
                })
                .catch(err => reject(err));
        });
    },
    initializeMemberImport: (shop, cms, file = null) =>
        new Promise((resolve, reject) => {
            if (file) file = _.removeDeepByKey(file, '__typename');
            let variables = { file };
            if (shop) variables.shop = parseInt(shop);
            if (cms) variables.cms = parseInt(cms);
            apolloClient
                .mutate({
                    mutation: InitializeMemberImportGql,
                    variables
                })
                .then(({ data }) => resolve(data.CP_InitializeMemberImport))
                .catch(err => reject(err));
        }),
    validateMemberImportCsv: (shop, cms, file) =>
        new Promise((resolve, reject) => {
            let variables = { file: _.removeDeepByKey(file, '__typename') };
            if (shop) variables.shop = parseInt(shop);
            if (cms) variables.cms = parseInt(cms);
            apolloClient
                .mutate({
                    mutation: ValidateMemberImportCsvGql,
                    variables
                })
                .then(({ data }) => resolve(data.CP_ValidateMemberImportCsv))
                .catch(err => reject(err));
        }),
    fetchApiTokens: memberId =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: MemberApiTokensGql,
                    variables: { memberId: parseInt(memberId) },
                    fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_MemberApiTokens))
                .catch(err => reject(err));
        }),
    createApiToken: (memberId, name) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateMemberApiTokenGql,
                    variables: { memberId: parseInt(memberId), name }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_RSTRCT_CreateMemberApiToken);
                })
                .catch(err => reject(err));
        }),
    deleteApiToken: (memberId, tokenId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteMemberApiTokenGql,
                    variables: { memberId: parseInt(memberId), tokenId: parseInt(tokenId) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_RSTRCT_DeleteMemberApiToken);
                })
                .catch(err => reject(err));
        }),
};
